import { SkeletonData, Spine } from "@pixi-spine/all-3.8";
import { ERoundState } from "../../core/round/interfaces";
import { GameAnimationData } from "../constants";
import {
    EBackgroundAnimationStates,
    ECrocoAnimationStates,
    EForegroundAnimationStates,
    ESignAnimationStates
} from "./enums";
import { Circ, gsap } from "gsap";
import { interval, Subscription } from "rxjs";
import { PixiApplication } from "@karma-games/angular-devkit/src/lib/modules/pixi";

export class CrocoGameApp extends PixiApplication {
    private backgroundAnimation: Spine | undefined = undefined;
    private foregroundAnimation: Spine | undefined = undefined;
    private crocoAnimation: Spine | undefined = undefined;
    private signAnimation: Spine | undefined = undefined;
    private currentRoundState: ERoundState = ERoundState.Idle;
    private backgroundSlowStopAnimation: gsap.core.Tween | undefined = undefined;
    private timeTick$: Subscription | undefined = undefined;
    private currentSecondsElapsed: number = 0;

    override initialize(gameContainer: HTMLElement, initialRoundState: ERoundState = ERoundState.Idle): void {
        super.initialize(gameContainer);
        this.view.style.maxWidth = "initial";
        this.view.style.maxHeight = "initial";
        this.view.style.minWidth = "100%";
        this.view.style.minHeight = "100%";
        this.backgroundAnimation = new Spine(<SkeletonData>this.loader!.resources[GameAnimationData.backgroundAnimationName].spineData);
        this.foregroundAnimation = new Spine(<SkeletonData>this.loader!.resources[GameAnimationData.foregroundAnimationName].spineData);
        this.crocoAnimation = new Spine(<SkeletonData>this.loader!.resources[GameAnimationData.crocoAnimationName].spineData);
        this.signAnimation = new Spine(<SkeletonData>this.loader!.resources[GameAnimationData.signAnimationName].spineData);
        this.stage.addChild(this.backgroundAnimation);
        this.stage.addChild(this.foregroundAnimation);
        this.stage.addChild(this.crocoAnimation);
        this.stage.addChild(this.signAnimation);

        this.backgroundAnimation.state.setAnimation(0, EBackgroundAnimationStates.Idle, true);
        this.foregroundAnimation.state.setAnimation(0, EForegroundAnimationStates.Idle, true);
        this.crocoAnimation.state.setAnimation(0, ECrocoAnimationStates.Idle, true);
        this.signAnimation.visible = false;
        this.changeState(initialRoundState);
    }

    changeState(state: ERoundState): void {
        if (!this.backgroundAnimation || !this.foregroundAnimation || !this.crocoAnimation || this.currentRoundState === state) {
            return;
        }
        this.currentRoundState = state;
        switch (state) {
            case ERoundState.Idle:
                if (this.backgroundSlowStopAnimation) {
                    this.backgroundSlowStopAnimation.kill();
                }
                this.backgroundAnimation!.state.timeScale = 1;
                this.foregroundAnimation.state.timeScale = 1;
                this.crocoAnimation.state.timeScale = 1;
                this.signAnimation!.visible = false;
                this.signAnimation!.state.timeScale = 1;
                this.backgroundAnimation!.state.setAnimation(0, EBackgroundAnimationStates.Idle, true);
                this.foregroundAnimation!.state.setAnimation(0, EForegroundAnimationStates.Idle, true);
                this.crocoAnimation!.state.setAnimation(0, ECrocoAnimationStates.Idle, true);
                break;
            case ERoundState.Playing:
                this.timeTick$ = interval(1000).subscribe(this.onTimeTick);
                this.backgroundAnimation!.state.setAnimation(0, EBackgroundAnimationStates.Run, true);
                this.foregroundAnimation!.state.setAnimation(0, EForegroundAnimationStates.Run, true);
                this.crocoAnimation!.state.setAnimation(0, ECrocoAnimationStates.Run, true);
                break;
            case ERoundState.Ended:
                if (this.timeTick$) {
                    this.timeTick$.unsubscribe();
                    this.currentSecondsElapsed = 0;
                }
                this.crocoAnimation.state.timeScale = 1;
                this.crocoAnimation!.state.setAnimation(0, ECrocoAnimationStates.Fall, false);
                this.backgroundAnimation.state.timeScale = 0;
                let num = { var: this.foregroundAnimation!.state.timeScale };
                this.backgroundSlowStopAnimation = gsap.to(num, {
                    duration: 1.5,
                    var: 0,
                    ease: Circ.easeOut,
                    onUpdate: () => {
                        this.signAnimation!.state.timeScale = num.var;
                        this.foregroundAnimation!.state.timeScale = num.var;
                    }
                });
                break;
            default:
                if (this.backgroundSlowStopAnimation) {
                    this.backgroundSlowStopAnimation.kill();
                }
                this.backgroundAnimation!.state.timeScale = 1;
                this.foregroundAnimation.state.timeScale = 1;
                this.crocoAnimation.state.timeScale = 1;
                this.backgroundAnimation!.state.setAnimation(0, EBackgroundAnimationStates.Idle, true);
                this.foregroundAnimation!.state.setAnimation(0, EForegroundAnimationStates.Idle, true);
                this.crocoAnimation!.state.setAnimation(0, ECrocoAnimationStates.Idle, true);
                break;
        }
    }

    triggerSignAnimation(animationState: ESignAnimationStates | undefined): void {
        if (animationState && this.signAnimation) {
            this.signAnimation.visible = true;
            this.signAnimation.state.setAnimation(0, animationState, false);
        }
    }

    onTimeTick = (): void => {
        this.currentSecondsElapsed += 1;
        const timeScale: number = 1.5 / 100 * (this.currentSecondsElapsed > 0 ? this.currentSecondsElapsed : 1);
        if (this.currentRoundState === ERoundState.Playing) {
            this.foregroundAnimation!.state.timeScale = timeScale + 1;
            this.crocoAnimation!.state.timeScale = timeScale + 1;
        }
    }
}
